import { authedFetch } from '@/providers/loginWithEmail/dataProvider';
import { AssignablePerson, RelatedPerson } from '@/types';

function parseError(data: unknown, fallback: string): string {
    if (!data || typeof data !== 'object') return fallback;
    const obj = data as Record<string, unknown>;
    const detail = obj.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        const root = (detail as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    return fallback;
}

async function authFetch(path: string, init?: RequestInit) {
    const res = await authedFetch(path, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            ...(init?.headers || {}),
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(parseError(data, 'Request failed'));
    }
    return data;
}

export async function fetchProjectRelatedPeople(
    projectId: number
): Promise<RelatedPerson[]> {
    const data = await authFetch(`/projects/${projectId}/related-people`);
    return data as RelatedPerson[];
}

export async function setProjectRelatedPeople(
    projectId: number,
    payload: {
        relatedPersonIds?: number[];
        contractorOwnerIds?: number[];
    },
): Promise<RelatedPerson[]> {
    const data = await authFetch(`/projects/${projectId}/related-people`, {
        method: 'PUT',
        body: JSON.stringify({
            related_person_ids: payload.relatedPersonIds ?? [],
            contractor_owner_ids: payload.contractorOwnerIds ?? [],
        }),
    });
    return data as RelatedPerson[];
}

export async function createRelatedPerson(payload: {
    name: string;
    email: string;
}): Promise<RelatedPerson> {
    const data = await authFetch(`/related-people`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return data as RelatedPerson;
}

export async function updateRelatedPerson(
    id: number,
    payload: { name: string; email: string },
): Promise<RelatedPerson> {
    const data = await authFetch(`/related-people/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return data as RelatedPerson;
}

export async function searchAssignablePeople(q: string): Promise<AssignablePerson[]> {
    const params = new URLSearchParams();
    if (q.trim()) {
        params.set('filter', JSON.stringify({ q: q.trim() }));
    }
    params.set('range', JSON.stringify([0, 24]));
    params.set('sort', JSON.stringify(['name', 'ASC']));
    const data = await authFetch(`/related-people/assignable?${params.toString()}`);
    return data as AssignablePerson[];
}
